<template>
  <div class="age-page">
    <div class="page-header">
      <h1 class="page-title">年龄计算器</h1>
      <p class="page-desc">输入出生日期，精确计算年龄</p>
    </div>

    <div class="calc-panel">
      <div class="input-row">
        <div class="input-group">
          <label>出生日期</label>
          <input type="date" v-model="birthDate" class="date-input" @change="calculate" />
        </div>
        <button class="now-btn" @click="setToday">今天</button>
      </div>

      <button class="calc-btn" @click="calculate">计算年龄</button>

      <transition name="fade">
        <div v-if="result" class="result-area">
          <div class="age-big">{{ result.years }} <span>岁</span></div>

          <div class="age-detail-grid">
            <div class="age-detail">
              <span class="detail-value">{{ result.totalMonths }}</span>
              <span class="detail-label">总月数</span>
            </div>
            <div class="age-detail">
              <span class="detail-value">{{ result.totalWeeks }}</span>
              <span class="detail-label">总周数</span>
            </div>
            <div class="age-detail">
              <span class="detail-value">{{ result.totalDays }}</span>
              <span class="detail-label">总天数</span>
            </div>
            <div class="age-detail">
              <span class="detail-value">{{ result.totalHours }}</span>
              <span class="detail-label">总小时数</span>
            </div>
          </div>

          <div class="age-exact">
            精确：<strong>{{ result.years }}年{{ result.months }}个月{{ result.days }}天</strong>
          </div>

          <div class="birthday-info" v-if="result.nextBirthday">
            <div class="next-birthday-label">下次生日</div>
            <div class="next-birthday-date">{{ result.nextBirthday }}</div>
            <div class="next-birthday-countdown">还有 <strong>{{ result.daysToBirthday }}</strong> 天</div>
          </div>

          <!-- 生肖星座 -->
          <div class="extra-info">
            <span class="extra-tag">生肖：{{ result.zodiac }}</span>
            <span class="extra-tag">星座：{{ result.constellation }}</span>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgePage',
  data() {
    return {
      birthDate: '2000-01-01',
      result: null
    }
  },
  methods: {
    setToday() {
      const now = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      this.birthDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
    },
    calculate() {
      if (!this.birthDate) return
      const birth = new Date(this.birthDate + 'T00:00:00')
      const now = new Date()

      if (isNaN(birth.getTime()) || birth > now) {
        this.result = null
        return
      }

      // 精确年月日
      let years = now.getFullYear() - birth.getFullYear()
      let months = now.getMonth() - birth.getMonth()
      let days = now.getDate() - birth.getDate()

      if (days < 0) {
        months--
        const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0)
        days += prevMonth.getDate()
      }
      if (months < 0) {
        years--
        months += 12
      }

      // 总数
      const diffMs = now - birth
      const totalDays = Math.floor(diffMs / 86400000)
      const totalHours = Math.floor(diffMs / 3600000)
      const totalWeeks = Math.floor(totalDays / 7)
      const totalMonths = years * 12 + months

      // 下次生日
      const thisYearBirthday = new Date(now.getFullYear(), birth.getMonth(), birth.getDate())
      let nextBirthday
      if (thisYearBirthday > now) {
        nextBirthday = thisYearBirthday
      } else {
        nextBirthday = new Date(now.getFullYear() + 1, birth.getMonth(), birth.getDate())
      }
      const daysToBirthday = Math.ceil((nextBirthday - now) / 86400000)

      const pad = (n) => String(n).padStart(2, '0')
      const nextBDStr = `${nextBirthday.getFullYear()}年${pad(nextBirthday.getMonth() + 1)}月${pad(nextBirthday.getDate())}日`

      // 生肖
      const zodiacAnimals = ['鼠', '牛', '虎', '兔', '龙', '蛇', '马', '羊', '猴', '鸡', '狗', '猪']
      const zodiac = zodiacAnimals[(birth.getFullYear() - 4) % 12]

      // 星座
      const constellation = this.getConstellation(birth.getMonth() + 1, birth.getDate())

      this.result = {
        years, months, days,
        totalDays, totalHours, totalWeeks, totalMonths,
        nextBirthday: nextBDStr,
        daysToBirthday,
        zodiac, constellation
      }
    },
    getConstellation(month, day) {
      const dates = [20, 19, 21, 20, 21, 22, 23, 23, 23, 24, 22, 22]
      const names = ['摩羯座', '水瓶座', '双鱼座', '白羊座', '金牛座', '双子座',
        '巨蟹座', '狮子座', '处女座', '天秤座', '天蝎座', '射手座', '摩羯座']
      return day < dates[month - 1] ? names[month - 1] : names[month]
    }
  }
}
</script>

<style scoped>
.age-page { max-width: 560px; margin: 0 auto; padding: 20px 16px 30px; }

.page-header { text-align: center; padding: 24px 0 12px; }
.page-title { font-size: 26px; font-weight: 700; color: #1a1a2e; }
.page-desc { font-size: 14px; color: #888; margin-top: 6px; }

.calc-panel {
  background: #fff; border-radius: 12px; padding: 28px 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06); text-align: center;
}

.input-row { display: flex; align-items: flex-end; gap: 12px; justify-content: center; margin-bottom: 20px; flex-wrap: wrap; }
.input-group { display: flex; flex-direction: column; gap: 6px; text-align: left; }
.input-group label { font-size: 13px; color: #888; font-weight: 500; }
.date-input { padding: 10px 14px; font-size: 16px; border: 2px solid #e0e0e0; border-radius: 8px; min-width: 180px; }
.date-input:focus { border-color: #1677ff; outline: none; }

.now-btn {
  padding: 10px 16px; font-size: 13px; background: #f0f5ff; color: #1677ff;
  border: 1px solid #b7d4ff; border-radius: 6px; cursor: pointer;
}
.now-btn:hover { background: #d6e8ff; }

.calc-btn {
  padding: 12px 40px; font-size: 16px; font-weight: 600;
  background: #1677ff; color: #fff; border: none; border-radius: 8px;
  cursor: pointer; transition: all 0.2s;
}
.calc-btn:hover { background: #4096ff; }

.result-area { margin-top: 24px; }

.age-big { font-size: 60px; font-weight: 700; color: #1677ff; line-height: 1.1; }
.age-big span { font-size: 24px; font-weight: 400; color: #888; }

.age-detail-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 20px; }
.age-detail {
  background: #f5f7fa; border-radius: 8px; padding: 12px 8px;
  display: flex; flex-direction: column; gap: 4px;
}
.detail-value { font-size: 20px; font-weight: 700; color: #1a1a2e; font-family: 'SF Mono', 'Consolas', monospace; }
.detail-label { font-size: 11px; color: #999; }

.age-exact { margin-top: 16px; font-size: 16px; color: #555; }

.birthday-info { margin-top: 20px; padding: 16px; background: linear-gradient(135deg, #fff7e6, #fffbe6); border-radius: 10px; }
.next-birthday-label { font-size: 13px; color: #888; }
.next-birthday-date { font-size: 18px; font-weight: 600; color: #333; margin-top: 4px; }
.next-birthday-countdown { font-size: 14px; color: #fa8c16; margin-top: 6px; }

.extra-info { display: flex; justify-content: center; gap: 16px; margin-top: 16px; flex-wrap: wrap; }
.extra-tag {
  font-size: 13px; background: #f0f5ff; color: #1677ff;
  padding: 5px 14px; border-radius: 20px;
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s, transform 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(-10px); }

@media (max-width: 480px) {
  .age-big { font-size: 44px; }
  .age-detail-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
